import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  REQUEST,
} from '@angular/core';
import { AuthService } from '@app/core/auth/auth.service';
import { NewAuthTokenResponse } from '@app/core/auth/interfaces/new-access-token-response';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Navigate } from '@ngxs/router-plugin';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthStateModel } from './auth-state.model';
import {
  BackendLogout,
  ChangePassword,
  ConfirmSignUp,
  ForgotPassword,
  FrontendLogout,
  ResetPassword,
  SaveAuthTokens,
  SetReturnUrl,
  SignIn,
  SignInWithGoogle,
  SignUp,
} from './auth.actions';
import { isPlatformServer } from '@angular/common';
import { CookieStorageEngine } from '@app/cookie-storage-engine';

@State<AuthStateModel>({
  name: 'auth',
})
@Injectable()
export class AuthState {
  constructor(
    private store: Store,
    private authService: AuthService,
    private cookieStorage: CookieStorageEngine,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: Request, // Inject the request object
  ) {}

  ngxsOnInit({ setState }: StateContext<AuthStateModel>): void {
    if (!isPlatformServer(this.platformId) || !this.request) {
      return;
    }
    const cookieName = 'auth';
    const cookie = this.cookieStorage.getItem(cookieName);
    if (cookie) {
      try {
        setState(JSON.parse(cookie));
      } catch (error) {
        console.error(`Error parsing ${cookieName} cookie in SSR:`, error);
      }
    }
  }

  @Action(SignIn)
  @ImmutableContext()
  signIn({ setState }: StateContext<AuthStateModel>, { payload }: SignIn) {
    return this.authService.signIn(payload).pipe(
      tap((response: NewAuthTokenResponse) => {
        return setState((state: AuthStateModel) => {
          state.token = response.access;
          state.refreshToken = response.refresh;
          state.accessTokenPayload = this.authService.decodeToken(
            response.access,
          );

          return state;
        });
      }),
    );
  }

  @Action(SignInWithGoogle)
  @ImmutableContext()
  signInWithGoogle(
    { setState }: StateContext<AuthStateModel>,
    { token }: SignInWithGoogle,
  ) {
    return this.authService.signInWithGoogle(token).pipe(
      tap((response: NewAuthTokenResponse) => {
        return setState((state: AuthStateModel) => {
          state.token = response.access;
          state.refreshToken = response.refresh;
          state.accessTokenPayload = this.authService.decodeToken(
            response.access,
          );

          return state;
        });
      }),
    );
  }

  @Action(BackendLogout)
  @ImmutableContext()
  backendLogout(
    { getState, setState }: StateContext<AuthStateModel>,
    { url }: BackendLogout,
  ) {
    const refreshToken = getState().refreshToken;
    const obs$ = refreshToken
      ? this.authService.logout({ refreshToken })
      : of(null);

    return obs$.pipe(
      tap(() => {
        setState((state: AuthStateModel) => {
          state.token = undefined;
          state.refreshToken = undefined;
          state.accessTokenPayload = undefined;

          return state;
        });

        this.store.dispatch(new Navigate([url]));
      }),
    );
  }

  @Action(FrontendLogout)
  @ImmutableContext()
  frontendLogout({ setState }: StateContext<AuthStateModel>) {
    setState((state: AuthStateModel) => {
      state.token = undefined;
      state.refreshToken = undefined;
      state.accessTokenPayload = undefined;

      return state;
    });

    this.store.dispatch(new Navigate(['/']));
  }

  @Action(SaveAuthTokens)
  @ImmutableContext()
  saveAuthTokens(
    { setState }: StateContext<AuthStateModel>,
    { payload }: SaveAuthTokens,
  ) {
    return setState((state: AuthStateModel) => {
      state.token = payload.access;
      state.accessTokenPayload = this.authService.decodeToken(payload.access);
      if (payload.refresh) {
        state.refreshToken = payload.refresh;
      }

      return state;
    });
  }

  @Action(SignUp)
  @ImmutableContext()
  register({}, { payload }: SignUp) {
    return this.authService.signUp(payload);
  }

  @Action(ConfirmSignUp)
  @ImmutableContext()
  confirmSignUp({}, { token }: ConfirmSignUp) {
    return this.authService.confirmSignUp(token);
  }

  // @Action(ConfirmEmail)
  // @ImmutableContext()
  // confirmEmail({}, { payload }: ConfirmEmail) {
  //   return this.authService.confirmEmail(payload);
  // }

  @Action(ChangePassword)
  @ImmutableContext()
  changePassword({}, { payload }: ChangePassword) {
    return this.authService.changePassword(payload);
  }

  @Action(SetReturnUrl)
  @ImmutableContext()
  setReturnUrl(
    { setState }: StateContext<AuthStateModel>,
    { url }: SetReturnUrl,
  ) {
    return setState((state: AuthStateModel) => {
      state.returnUrl = url;

      return state;
    });
  }

  @Action(ForgotPassword)
  @ImmutableContext()
  forgotPassword(
    {}: StateContext<AuthStateModel>,
    { payload }: ForgotPassword,
  ) {
    return this.authService.forgotPassword(payload);
  }

  @Action(ResetPassword)
  @ImmutableContext()
  resetPassword({}: StateContext<AuthStateModel>, { payload }: ResetPassword) {
    return this.authService.resetPassword(payload);
  }
}
